/**
 * @file Error types and codes for the application
 * @description This file contains enumerations of stable error CODES used throughout the application.
 * The values are now the codes themselves, making them reliable for client-side logic.
 * @packageDocumentation
 */

/* eslint-disable max-len */

/**
 * Enumeration of common HTTP and application-level error CODES.
 *
 * @enum {string}
 */
export enum ERES_ERR {
  // The value is now the code itself, not the rigid message.
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  UNAUTHORIZED = 'UNAUTHORIZED',
  UNPROCESSABLE = 'UNPROCESSABLE',
  NOT_FOUND = 'NOT_FOUND',
  SERVER_ERROR = 'SERVER_ERROR',
  BAD_REQUEST = 'BAD_REQUEST',
  FORBIDDEN = 'FORBIDDEN',
  CONFLICT = 'CONFLICT',
  UNAVAILABLE = 'UNAVAILABLE',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  BAD_GATEWAY = 'BAD_GATEWAY',
  GATEWAY_TIMEOUT = 'GATEWAY_TIMEOUT',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  NOT_IMPLEMENTED = 'NOT_IMPLEMENTED',
  BAD_REQUEST_PARAMS = 'BAD_REQUEST_PARAMS',
  BAD_REQUEST_BODY = 'BAD_REQUEST_BODY',
  BAD_REQUEST_QUERY = 'BAD_REQUEST_QUERY',
  BAD_REQUEST_HEADER = 'BAD_REQUEST_HEADER',
  BAD_REQUEST_COOKIE = 'BAD_REQUEST_COOKIE',
  BAD_REQUEST_FILE = 'BAD_REQUEST_FILE',
  BAD_REQUEST_FILE_COUNT = 'BAD_REQUEST_FILE_COUNT',
  BAD_REQUEST_FILE_SIZE = 'BAD_REQUEST_FILE_SIZE',
  BAD_REQUEST_FILE_TYPE = 'BAD_REQUEST_FILE_TYPE',
  BAD_REQUEST_FILE_NAME = 'BAD_REQUEST_FILE_NAME',
  BAD_REQUEST_FILE_TMP_PATH = 'BAD_REQUEST_FILE_TMP_PATH',
  BAD_REQUEST_FILE_MD5 = 'BAD_REQUEST_FILE_MD5',
  BAD_REQUEST_FILE_SHA256 = 'BAD_REQUEST_FILE_SHA256',
  BAD_REQUEST_FILE_SHA512 = 'BAD_REQUEST_FILE_SHA512',
  BAD_REQUEST_FILE_SHA1 = 'BAD_REQUEST_FILE_SHA1',
  BAD_REQUEST_FILE_SHA224 = 'BAD_REQUEST_FILE_SHA224',
  BAD_REQUEST_FILE_SHA384 = 'BAD_REQUEST_FILE_SHA384',
  BAD_REQUEST_FILE_SHA3_256 = 'BAD_REQUEST_FILE_SHA3_256',
  BAD_REQUEST_FILE_SHA3_384 = 'BAD_REQUEST_FILE_SHA3_384',
  BAD_REQUEST_FILE_SHA3_512 = 'BAD_REQUEST_FILE_SHA3_512',
  LIMIT_FILE_SIZE = 'LIMIT_FILE_SIZE',
  FILE_TYPE_NOT_ALLOWED = 'FILE_TYPE_NOT_ALLOWED',
  TOO_MANY_ATTEMPTS = 'TOO_MANY_ATTEMPTS',
  INVALID_INPUT = 'INVALID_INPUT',
}

/**
 * Enumeration of 401 Unauthorized error CODES.
 *
 * @enum {string}
 */
export enum ERES_ERR_LOCAL_401 {
  // Currently no specific 401 errors defined
}

/**
 * Enumeration of 403 Forbidden error CODES.
 *
 * @enum {string}
 */
export enum ERES_ERR_LOCAL_403 {
  // The value is now the code itself, making it reliable for matching.
  INVALID_ID = 'LOCAL_403_INVALID_ID',
  NO_IDS = 'LOCAL_403_NO_IDS',
  INVALID_IDS = 'LOCAL_403_INVALID_IDS',
  NO_ID = 'LOCAL_403_NO_ID',
  USER_LINKED = 'LOCAL_403_USER_LINKED',
  USERS_LINKED = 'LOCAL_403_USERS_LINKED',
  ACCOUNT_NOT_EXISTS = 'LOCAL_403_ACCOUNT_NOT_EXISTS',
  ACCOUNT_BLOCK = 'LOCAL_403_ACCOUNT_BLOCK',
  UNKNWON_IP = 'LOCAL_403_UNKNOWN_IP',
  PASSWORD_TOO_EASY = 'LOCAL_403_PASSWORD_TOO_EASY',
  PASSWORD_COMMON = 'LOCAL_403_PASSWORD_COMMON',
  PASSWORD_SHOULD_NOT_MATCH_PART_OF_EMAIL = 'LOCAL_403_PASSWORD_NO_EMAIL_MATCH',
  PASSWORD_SHOULD_NOT_MATCH_PART_OF_PHONE_NUMBER = 'LOCAL_403_PASSWORD_NO_PHONE_MATCH',
  PASSWORD_SHOULD_CONTAIN_AT_LEAST_ONE_SYMBOL_ONE_UPPERCASE_ONE_LOWERCASE_AND_ONE_NUMERIC_VALUE = 'LOCAL_403_PASSWORD_COMPLEXITY_FAILED',
  PASSWORD_TOO_SHORT = 'LOCAL_403_PASSWORD_TOO_SHORT',
  PASSWORD_TOO_LONG = 'LOCAL_403_PASSWORD_TOO_LONG',
  PASSWORD_TOO_WEAK = 'LOCAL_403_PASSWORD_TOO_WEAK',
  PASSWORD_TOO_STRONG = 'LOCAL_403_PASSWORD_TOO_STRONG',
  PASSWORD_TOO_WEIRD = 'LOCAL_403_PASSWORD_TOO_WEIRD',
  SPONSORED_MUST_HAVE_ITEMS = 'LOCAL_403_SPONSORED_NO_ITEMS',
  INVALID_TOKEN = 'LOCAL_403_INVALID_TOKEN',
  INVALID_CODE = 'LOCAL_403_INVALID_CODE',
  INVALID_VERIFICATION_LINK = 'LOCAL_403_INVALID_VERIFICATION_LINK',
  VERIFICATION_TIMEOUT = 'LOCAL_403_VERIFICATION_TIMEOUT',
  MAX_VERIFICATION_ATTEMPTS = 'LOCAL_403_MAX_VERIFICATION_ATTEMPTS',
  INVALID_VERIFICATION_CODE = 'LOCAL_403_INVALID_VERIFICATION_CODE',
  NO_COUNTRY_CODE = 'LOCAL_403_NO_COUNTRY_CODE',
  INVALID_REMEMBER_ME = 'LOCAL_403_INVALID_REMEMBER_ME',
  INVALID_REMEMBER_ME_DAYS_LESS = 'LOCAL_403_REMEMBER_ME_DAYS_LESS',
  INVALID_REMEMBER_ME_DAYS_GREATER = 'LOCAL_403_REMEMBER_ME_DAYS_GREATER',
  ACCOUNT_NOT_VERIFIED = 'LOCAL_403_ACCOUNT_NOT_VERIFIED',
  ACCOUNT_NOT_VERIFIED_EMAIL = 'LOCAL_403_ACCOUNT_NOT_VERIFIED_EMAIL',
  ACCOUNT_NOT_VERIFIED_PHONE = 'LOCAL_403_ACCOUNT_NOT_VERIFIED_PHONE',
  ACCOUNT_NOT_VERIFIED_PASSWORDLESS_EMAIL = 'LOCAL_403_ACCOUNT_NOT_VERIFIED_PASSWORDLESS_EMAIL',
  ACCOUNT_NOT_VERIFIED_PASSWORDLESS_PHONE = 'LOCAL_403_ACCOUNT_NOT_VERIFIED_PASSWORDLESS_PHONE',
  PENDING_FIRST_SUBSCRIPTION = 'LOCAL_403_PENDING_FIRST_SUBSCRIPTION',
  PENDING_SUBSCRIPTION = 'LOCAL_403_PENDING_SUBSCRIPTION',
  COMPANY_NOT_VERIFIED = 'LOCAL_403_COMPANY_NOT_VERIFIED',
  COMPANY_SUSPENDED_DUE_TO_SUBSCRIPTION = 'LOCAL_403_COMPANY_SUSPENDED_DUE_TO_SUBSCRIPTION',
  COMPANY_BLOCKED = 'LOCAL_403_COMPANY_BLOCKED',
  COMPANY_LEFT = 'LOCAL_403_COMPANY_LEFT',
  SUBSCRIPTION_INACTIVE_OR_EXPIRED = 'LOCAL_401_SUBSCRIPTION_INACTIVE_OR_EXPIRED',
  SUBSCRIPTION_FEATURE_LIMIT_EXCEEDED = 'LOCAL_401_SUBSCRIPTION_FEATURE_LIMIT_EXCEEDED',
}

/**
 * Enumeration of file-related error messages.
 * These errors are specific to file operations and uploads.
 *
 * @enum {string}
 */
export enum ERS_ERR_FILE {
  // Currently no specific file errors defined
}
